import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Star } from "lucide-react";
import { api } from "@/lib/api";

// Reusable Smart Match panel. Feed it the current visible listings + kind
// (jobs|property|doctors) and the user's saved smart profile. Renders the
// top 5 matches with reasoning + verified badges. Works even for logged-out
// users because /api/smart/match doesn't require auth (profile can be empty).
export default function SmartMatchPanel({ kind, listings, initialProfile = {}, title = "Smart matches for you" }) {
  const [profile, setProfile] = useState(initialProfile);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  // Auto-load saved profile once — non-blocking.
  useEffect(() => {
    api.get("/smart/profile")
       .then((r) => { if (r.data?.profile) setProfile((p) => ({ ...r.data.profile, ...p })); })
       .catch(() => {});
  }, []);

  const run = async () => {
    if (!listings?.length) return;
    setBusy(true);
    try {
      const r = await api.post("/smart/match", { kind, profile, listings });
      setResults(r.data.results);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (listings?.length) run();
  }, [listings?.length, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!listings?.length) return null;

  return (
    <div className="rounded-3xl bg-gradient-to-br from-fuchsia-50 to-purple-50 border border-fuchsia-200 p-5 md:p-6" data-testid={`smart-match-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="heading text-lg font-black text-stone-900 inline-flex items-center gap-2">
          <Sparkles size={16} className="text-fuchsia-600" /> {title}
        </h3>
        <button onClick={run} className="text-xs font-bold px-3 py-1.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="smart-refresh">
          {busy ? <Loader2 size={13} className="animate-spin" /> : "Refresh"}
        </button>
      </div>
      {results.length === 0 ? (
        <div className="text-sm text-stone-500 py-4 text-center">Tap Refresh — we'll rank the visible listings against your profile.</div>
      ) : (
        <ul className="space-y-2">
          {results.slice(0, 5).map((r, i) => (
            <li key={r.id + i} className="rounded-2xl bg-white border border-stone-200 p-3 flex items-start gap-3" data-testid={`smart-result-${r.id}`}>
              <div className={`w-12 h-12 rounded-xl grid place-items-center font-black text-white ${r.score >= 80 ? "bg-emerald-600" : r.score >= 60 ? "bg-amber-500" : "bg-stone-400"}`}>
                {r.score}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-stone-900 truncate">{r.title || r.id}</div>
                <div className="text-[11px] text-stone-500 line-clamp-2">{r.reasons.slice(0, 3).join(" · ")}</div>
                {r.badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.badges.map((b) => (
                      <span key={b} className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                        <Star size={9} /> {b.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
